import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import {
  createInvite,
  linkAmoCRMUser,
  listAmoCRMUsers,
  listInvites,
  listOperators,
  listRoles,
  updateOperator,
  type CreateInviteResponse,
  type OperatorAccount,
} from '@/entities/user';
import { useAuth } from '@/features/auth';
import { ApiError } from '@/shared/api';
import { formatDateTime, labelOf, useI18n } from '@/shared/i18n';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/shadcn/card';
import { Input } from '@/shared/ui/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/shadcn/table';
import { PageHeader } from '@/widgets/page-header';

const permissionKeys = [
  'can_view_chats',
  'can_view_crm',
  'can_use_playground',
  'can_edit_knowledge_base',
  'can_view_remarks',
  'can_manage_users',
  'can_invite_users',
] as const;

export function UsersPage() {
  const { t, locale } = useI18n();
  const { user: currentUser, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('users');
  const [editing, setEditing] = useState<OperatorAccount | null>(null);
  const [name, setName] = useState('');
  const [roleKey, setRoleKey] = useState('teacher');
  const [inviteRole, setInviteRole] = useState('teacher');
  const [lastInvite, setLastInvite] = useState<CreateInviteResponse | null>(null);

  const usersQuery = useQuery({ queryKey: ['operators'], queryFn: listOperators });
  const rolesQuery = useQuery({ queryKey: ['operator-roles'], queryFn: listRoles });
  const invitesQuery = useQuery({ queryKey: ['invites'], queryFn: listInvites });
  const amoQuery = useQuery({
    queryKey: ['amocrm-users'],
    queryFn: listAmoCRMUsers,
    enabled: tab === 'amocrm',
  });
  const linkAmoMutation = useMutation({
    mutationFn: (input: { amocrmUserId: number; agataUserId: string | null }) =>
      linkAmoCRMUser(input.amocrmUserId, input.agataUserId),
    onSuccess: () => {
      toast.success(t.users.amoLinked);
      void queryClient.invalidateQueries({ queryKey: ['amocrm-users'] });
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : t.users.amoLinkFailed);
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => updateOperator(editing!.id, { name: name.trim(), role_key: roleKey }),
    onSuccess: (updated) => {
      if (currentUser?.id === updated.id) {
        updateUser({ ...currentUser, name: updated.name, role_key: updated.role_key });
      }
      setEditing(null);
      toast.success(t.users.saved);
      void queryClient.invalidateQueries({ queryKey: ['operators'] });
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : t.users.saveFailed);
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () => createInvite(inviteRole),
    onSuccess: (invite) => {
      setLastInvite(invite);
      toast.success(t.users.inviteCreated);
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : t.users.inviteFailed);
    },
  });

  const roles = rolesQuery.data ?? [];
  const roleOptions =
    roles.length > 0 ? roles.map((role) => role.key) : ['admin', 'teacher', 'sales'];

  const startEdit = (account: OperatorAccount) => {
    setEditing(account);
    setName(account.name);
    setRoleKey(account.role_key);
  };

  const copyInvite = async (path: string) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      toast.success(t.users.inviteCopied);
    } catch {
      toast.error(t.users.copyFailed);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t.users.title} description={t.users.description} />
      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 border-b px-4 py-2">
          <TabsList>
            <TabsTrigger value="users">{t.users.list}</TabsTrigger>
            <TabsTrigger value="roles">{t.users.roles}</TabsTrigger>
            <TabsTrigger value="invites">{t.users.invites}</TabsTrigger>
            <TabsTrigger value="amocrm">{t.users.amocrm}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="users" className="min-h-0 flex-1 overflow-hidden p-0 data-[hidden]:hidden">
          <ScrollArea className="h-full" viewportClassName="p-4">
          {usersQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : usersQuery.isError ? (
            <p className="text-sm text-destructive">{t.users.loadFailed}</p>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.common.name}</TableHead>
                    <TableHead>{t.common.email}</TableHead>
                    <TableHead>{t.common.role}</TableHead>
                    <TableHead>{t.common.created}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(usersQuery.data ?? []).map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>{account.name}</TableCell>
                      <TableCell>{account.email}</TableCell>
                      <TableCell>{labelOf(t.roles, account.role_key)}</TableCell>
                      <TableCell>{formatDateTime(account.created_at, locale)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(account)}
                        >
                          {t.users.edit}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(usersQuery.data?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">{t.users.empty}</p>
              ) : null}
              {editing ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{editing.email}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap items-end gap-3">
                    <div className="space-y-2">
                      <p className="text-sm font-medium">{t.common.name}</p>
                      <Input value={name} onChange={(event) => setName(event.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium">{t.common.role}</p>
                      <Select
                        value={roleKey}
                        onValueChange={(value) => {
                          if (value) setRoleKey(value);
                        }}
                      >
                        <SelectTrigger className="w-44">
                          <SelectValue>{(value) => labelOf(t.roles, value)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {roleOptions.map((key) => (
                            <SelectItem key={key} value={key}>
                              {labelOf(t.roles, key)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      disabled={saveMutation.isPending || !name.trim()}
                      onClick={() => saveMutation.mutate()}
                    >
                      {saveMutation.isPending ? t.common.saving : t.users.save}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                      {t.users.cancel}
                    </Button>
                  </CardContent>
                </Card>
              ) : null}
            </div>
          )}
          </ScrollArea>
        </TabsContent>
        <TabsContent value="roles" className="min-h-0 flex-1 overflow-hidden p-0 data-[hidden]:hidden">
          <ScrollArea className="h-full" viewportClassName="p-4">
          {rolesQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="grid gap-3 desktop:grid-cols-3">
              {roles.map((role) => (
                <Card key={role.key}>
                  <CardHeader>
                    <CardTitle className="text-base">{labelOf(t.roles, role.key)}</CardTitle>
                    <CardDescription>
                      {t.roles.descriptions[role.key as keyof typeof t.roles.descriptions] ??
                        role.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-1.5">
                    {permissionKeys
                      .filter((key) => role.permissions[key])
                      .map((key) => (
                        <Badge key={key} variant="outline" className="font-normal">
                          {t.permissions[key]}
                        </Badge>
                      ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          </ScrollArea>
        </TabsContent>
        <TabsContent value="amocrm" className="min-h-0 flex-1 overflow-hidden p-0 data-[hidden]:hidden">
          <ScrollArea className="h-full" viewportClassName="p-4">
          {amoQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : amoQuery.isError ? (
            <p className="text-sm text-destructive">{t.users.amoLoadFailed}</p>
          ) : (amoQuery.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">{t.users.amoEmpty}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.common.name}</TableHead>
                  <TableHead>{t.common.email}</TableHead>
                  <TableHead>{t.users.amoStatus}</TableHead>
                  <TableHead>{t.users.agataUser}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(amoQuery.data ?? []).map((account) => (
                  <TableRow key={account.amocrm_user_id}>
                    <TableCell>{account.name}</TableCell>
                    <TableCell>{account.email}</TableCell>
                    <TableCell>
                      {account.is_active ? t.users.amoActive : t.users.amoInactive}
                      {account.is_admin ? ` · ${t.users.amoAdmin}` : ''}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={account.agata_user_id ?? '__none__'}
                        onValueChange={(value) => {
                          if (!value) return;
                          linkAmoMutation.mutate({
                            amocrmUserId: account.amocrm_user_id,
                            agataUserId: value === '__none__' ? null : value,
                          });
                        }}
                      >
                        <SelectTrigger className="w-56">
                          <SelectValue>
                            {(value) => {
                              if (!value || value === '__none__') return t.users.amoUnlinked;
                              const op = (usersQuery.data ?? []).find((item) => item.id === value);
                              return op ? `${op.name} (${op.email})` : value;
                            }}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t.users.amoUnlinked}</SelectItem>
                          {(usersQuery.data ?? []).map((op) => (
                            <SelectItem key={op.id} value={op.id}>
                              {op.name} ({op.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          </ScrollArea>
        </TabsContent>
        <TabsContent value="invites" className="min-h-0 flex-1 overflow-hidden p-0 data-[hidden]:hidden">
          <ScrollArea className="h-full" viewportClassName="p-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t.users.invites}</CardTitle>
              <CardDescription>{t.users.invitesHint}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t.common.role}</p>
                  <Select
                    value={inviteRole}
                    onValueChange={(value) => {
                      if (value) setInviteRole(value);
                    }}
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue>{(value) => labelOf(t.roles, value)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((key) => (
                        <SelectItem key={key} value={key}>
                          {labelOf(t.roles, key)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  disabled={inviteMutation.isPending}
                  onClick={() => inviteMutation.mutate()}
                >
                  {inviteMutation.isPending ? t.users.creatingInvite : t.users.createInvite}
                </Button>
              </div>
              {lastInvite ? (
                <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm">
                  <code className="min-w-0 flex-1 break-all">
                    {`${window.location.origin}${lastInvite.invite_path}`}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void copyInvite(lastInvite.invite_path)}
                  >
                    <Copy className="size-4" />
                    {t.common.copy}
                  </Button>
                </div>
              ) : null}
              <div className="space-y-2">
                <p className="text-sm font-medium">{t.users.pending}</p>
                {invitesQuery.isLoading ? (
                  <Skeleton className="h-24 w-full" />
                ) : invitesQuery.isError ? (
                  <p className="text-sm text-destructive">{t.users.invitesLoadFailed}</p>
                ) : (invitesQuery.data?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">{t.users.noPending}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t.common.role}</TableHead>
                        <TableHead>{t.common.expires}</TableHead>
                        <TableHead>{t.common.created}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invitesQuery.data?.map((invite) => (
                        <TableRow key={invite.id}>
                          <TableCell>{labelOf(t.roles, invite.role_key)}</TableCell>
                          <TableCell>{formatDateTime(invite.expires_at, locale)}</TableCell>
                          <TableCell>{formatDateTime(invite.created_at, locale)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
